import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { Domains, getDomain } from '@sembo-private/utils';
import { uniqBy } from 'lodash';

import { injectStrict } from '@apps/main/modules/helpers/injectStrict';
import { RecipeStateKey, TripStateKey } from '@apps/main/modules/state/state';
import { useItineraryStore } from '@apps/main/modules/store/itineraryStore';
import {
  useBookTripStore,
  usePaxStore,
  useShareWidgetStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { usePreferredTimeStore } from '@apps/main/modules/store/preferredTimeStore';

import type { AppButtonState } from '@libs/ui';
import { getMarketOrigin } from '@libs/search-service-smart-search';

import type { TripSummarySegment } from '../models';

import {
  getTravelDateRange,
  getTripSummaryPriceSegments,
  isEveryIngredientAvailableFn,
} from './tripSummaryStoreLogic';
import { useTripOverviewModalsStore } from './tripOverviewModalsStore';
import { useUpsellStore } from './upsell/upsellStore';

export const useTripSummaryStore = defineStore('TripSummaryStore', () => {
  const recipeState = injectStrict(RecipeStateKey);
  const tripState = injectStrict(TripStateKey);

  const itineraryStore = useItineraryStore();
  const shareWidgetStore = useShareWidgetStore();
  const bookTripStore = useBookTripStore();
  const statsigEventsStore = useStatsigEventsStore();
  const preferredTimeStore = usePreferredTimeStore();
  const tripOverviewModalsStore = useTripOverviewModalsStore();
  const paxStore = usePaxStore();
  const upsellStore = useUpsellStore();

  const isTripSummaryVisible = ref<boolean>(true);

  // Getters
  const travelDateRange = computed<string>(() =>
    getTravelDateRange(tripState.chronologicalItinerary.value)
  );

  const isEveryIngredientAvailable = computed<boolean>(
    () =>
      isEveryIngredientAvailableFn(itineraryStore.selectedItinerary) &&
      tripTotalPrice.value > 0
  );

  const tripPriceSegments = computed<TripSummarySegment[]>(() =>
    getTripSummaryPriceSegments(
      tripState.chronologicalItinerary.value,
      paxStore.numberOfTravelers,
      upsellStore.userSelectedFerryTicketCategory
    )
  );

  const tripTotalPrice = computed<number>(() =>
    tripPriceSegments.value.reduce((acc, curr) => {
      curr.price.forEach((el) => (acc = acc + el.amount));
      return acc;
    }, 0)
  );

  const tripSummaryLoader = computed<number>(
    () =>
      uniqBy(
        recipeState.recipe.value,
        (ingredient) => ingredient.ingredientType
      ).length
  );

  const bookingButtonState = computed<AppButtonState>(() => {
    if (bookTripStore.bookingInProgress) {
      return 'loadingDots';
    }

    if (isEveryIngredientAvailable.value) {
      return 'enabled';
    }

    return 'disabled';
  });

  const PRICE_GUARANTEE_MARKETS: Domains[] = [
    Domains.SWEDEN,
    Domains.DENMARK,
    Domains.UNITEDKINGDOM,
    Domains.NORWAY,
    Domains.GERMANY,
  ];
  const shouldShowPriceGuarantee = computed<boolean>(() => {
    const currentDomain = getDomain.getCurrentDomainSpec().Domain;

    return PRICE_GUARANTEE_MARKETS.some(
      (market) => getDomain.getDomainSpec(market).Domain === currentDomain
    );
  });

  // Actions
  function onBookClicked(): void {
    statsigEventsStore.logTripOverviewBookClicked({
      totalIngredients: recipeState.amountOfIngredientsInRecipe.value,
      totalOwnStay: recipeState.ownStayOnlyRecipe.value.length,
      totalAccomodation: recipeState.hotelOnlyRecipe.value.length,
      totalFerry: recipeState.ferryOnlyRecipe.value.length,
      totalFlight: recipeState.flightOnlyRecipe.value.length,
      totalActivity: recipeState.stayOnlyRecipe.value.flatMap(
        (hotel) => hotel.subIngredients
      ).length,
      market: getMarketOrigin(),
      totalAmount: tripTotalPrice.value,
    });

    const isUserSelectedDate = !!preferredTimeStore.preferredDay;
    if (isUserSelectedDate) {
      bookTripStore.navigateToCheckout();
    } else {
      statsigEventsStore.logTripOverviewContinueWithSelectedDateModalOpened();
      tripOverviewModalsStore.openContinueWithSuggestedDateModal(
        travelDateRange.value,
        itineraryStore.itineraryNumberOfNights
      );
    }
  }

  function openShareWidget(): void {
    shareWidgetStore.openModal();
    statsigEventsStore.logTripOverviewShareOrSaveButtonClicked();
  }

  return {
    // State
    isTripSummaryVisible,

    // Getters
    travelDateRange,
    tripSummaryLoader,
    bookingButtonState,
    shouldShowPriceGuarantee,
    tripPriceSegments,
    tripTotalPrice,
    isEveryIngredientAvailable,

    // Actions
    onBookClicked,
    openShareWidget,
  };
});
